import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useDevicesApi } from '../api/context'
import { DEVICE_QUERY_KEYS } from '../api/endpoints'
import type { PaginatedDevicesResponse } from '../types/api.types'
import type {
  CreateDevicePayload,
  DeviceButtonPayload,
  DeviceMassEditItem,
  ProvisioningDeviceDetail,
  UpdateDevicePayload,
} from '../types/device.types'
import { sanitizeDeviceButtonsForApi } from '../utils/buttons'
import { mapDeviceWritePayload } from '../utils/devicePayload'
import { toApiError } from '../utils/formatApiError'
import { resolveExtensionId } from '../utils/helpers'

export function useDeleteDevice(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    onMutate: async (deviceId: number) => {
      await queryClient.cancelQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })

      const previous = queryClient.getQueriesData<PaginatedDevicesResponse>({
        queryKey: DEVICE_QUERY_KEYS.lists(),
      })

      queryClient.setQueriesData<PaginatedDevicesResponse>(
        { queryKey: DEVICE_QUERY_KEYS.lists() },
        (current) => {
          if (!current) return current
          const filtered = current.results.filter((d) => d.id !== deviceId)
          if (filtered.length === current.results.length) return current
          return {
            ...current,
            results: filtered,
            count: Math.max(0, (current.count ?? filtered.length) - 1),
          }
        },
      )

      queryClient.removeQueries({
        queryKey: DEVICE_QUERY_KEYS.detail(pbxId, deviceId),
      })

      return { previous }
    },
    mutationFn: async (deviceId: number) => {
      const response = await api.deleteDevice(pbxId, deviceId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to delete device')
      }
    },
    onError: (_error, _deviceId, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useRebuildDevices(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.rebuildDevices(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to rebuild devices')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useRebuildAndRebootDevices(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.rebuildAndRebootDevices(pbxId)
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to rebuild and reboot devices')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useMassEditDevices(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (vars: {
      payload: DeviceMassEditItem[]
      reboot: boolean
    }) => {
      const { payload, reboot } = vars
      const response = await api.massEditDevices(
        pbxId,
        payload as Record<string, unknown>[],
      )
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to mass edit devices')
      }

      const deviceIds = [...new Set(payload.map((p) => p.id))]
      let reloadRebootWarning: string | null = null

      try {
        if (reboot) {
          const rebootResponse = await api.rebootDevice(pbxId, deviceIds)
          if (!rebootResponse.ok) {
            reloadRebootWarning = 'Devices updated, but reboot request failed.'
          }
        } else {
          const reloadResponse = await api.reloadDeviceConfig(pbxId, deviceIds)
          if (!reloadResponse.ok) {
            reloadRebootWarning =
              'Devices updated, but config reload request failed.'
          }
        }
      } catch (err) {
        console.warn('Config reload/reboot failed:', err)
        reloadRebootWarning = reboot
          ? 'Devices updated, but reboot request failed.'
          : 'Devices updated, but config reload request failed.'
      }

      return { warning: reloadRebootWarning }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useReloadConfiguration(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deviceIds: number[]) => {
      const response = await api.reloadDeviceConfig(pbxId, deviceIds)
      if (!response.ok) {
        throw new Error(
          `Failed to reload config for ${deviceIds.length} device(s)`,
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useReloadConfigurationAndReboot(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deviceIds: number[]) => {
      const response = await api.rebootDevice(pbxId, deviceIds)
      if (!response.ok) {
        throw new Error(`Failed to reload/reboot ${deviceIds.length} device(s)`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useSetDesiredFirmwareVersionAndReload(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      desired_firmware_version: string
      device_ids: number[]
    }) => {
      const results = await Promise.allSettled(
        params.device_ids.map(async (deviceId) => {
          const updateResponse = await api.updateDevice(pbxId, deviceId, {
            desired_firmware_version: params.desired_firmware_version,
          })
          if (!updateResponse.ok) {
            throw { deviceId, error: updateResponse.data }
          }
          const reloadResponse = await api.reloadDeviceConfig(pbxId, [deviceId])
          if (!reloadResponse.ok) {
            throw { deviceId, error: reloadResponse.data }
          }
        }),
      )
      const errors = results
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r) => r.reason)
      if (errors.length > 0) {
        throw new Error(`Failed to set firmware for ${errors.length} device(s)`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useCreateDevice(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateDevicePayload) => {
      const response = await api.createDevice(
        pbxId,
        mapDeviceWritePayload(payload, { isCreate: true }),
      )
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to create device')
      }
      const device = response.data as ProvisioningDeviceDetail

      try {
        const reloadRes = await api.reloadDeviceConfig(pbxId, [device.id])
        if (!reloadRes.ok) {
          console.warn(
            'Initial config reload returned non-ok after device create',
          )
        }
      } catch (err) {
        console.warn('Initial config reload failed after device create:', err)
      }

      return device
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}

export function useUpdateDevice(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      deviceId: number
      payload: UpdateDevicePayload
      reboot?: boolean
    }) => {
      const response = await api.updateDevice(
        pbxId,
        params.deviceId,
        mapDeviceWritePayload(params.payload),
      )
      if (!response.ok) {
        throw toApiError(response.data, 'Failed to update device')
      }

      let reloadRebootWarning: string | null = null

      try {
        if (params.reboot) {
          const rebootResponse = await api.rebootDevice(pbxId, [
            params.deviceId,
          ])
          if (!rebootResponse.ok) {
            reloadRebootWarning = 'Device updated, but reboot request failed.'
          }
        } else {
          const reloadResponse = await api.reloadDeviceConfig(pbxId, [
            params.deviceId,
          ])
          if (!reloadResponse.ok) {
            reloadRebootWarning =
              'Device updated, but config reload request failed.'
          }
        }
      } catch (err) {
        console.warn('Config reload/reboot failed:', err)
        reloadRebootWarning = params.reboot
          ? 'Device updated, but reboot request failed.'
          : 'Device updated, but config reload request failed.'
      }

      return {
        device: response.data as ProvisioningDeviceDetail,
        warning: reloadRebootWarning,
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
      queryClient.invalidateQueries({
        queryKey: DEVICE_QUERY_KEYS.detail(pbxId, variables.deviceId),
      })
    },
  })
}

export function useCloneDevice(pbxId: number) {
  const api = useDevicesApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      sourceDeviceId: number
      payload: CreateDevicePayload
      sourceButtons: DeviceButtonPayload[]
      modelSupportsSidecar?: boolean
      maxLineButtons?: number
    }) => {
      const shouldCopySidecar =
        params.modelSupportsSidecar === true &&
        params.payload.sidecar_enabled === true

      const newPrimaryExtId = params.payload.primary_extension_id

      const rawButtons: DeviceButtonPayload[] = params.sourceButtons
        .filter(
          (btn) =>
            btn.type === 'LineButton' ||
            (btn.type === 'SidecarButton' && shouldCopySidecar),
        )
        .map((btn) => {
          let buttonValue = btn.button_value
          if (
            newPrimaryExtId &&
            btn.button_function === 'presence' &&
            btn.button_value === String(newPrimaryExtId)
          ) {
            buttonValue = undefined
          }
          const extensionPk = resolveExtensionId(btn.extension)
          return {
            type: btn.type,
            display_count: btn.display_count,
            button_function: btn.button_function,
            button_value: buttonValue,
            button_label: btn.button_label,
            extension: extensionPk,
            speed_dial_value: btn.speed_dial_value,
            slot_number: btn.slot_number,
          }
        })

      const buttons = sanitizeDeviceButtonsForApi(rawButtons, {
        primaryExtensionId: newPrimaryExtId,
        includePrimaryLineFallback: true,
        includeSidecarButtons: shouldCopySidecar,
        maxLineButtons: params.maxLineButtons,
      })

      const createRes = await api.createDevice(
        pbxId,
        mapDeviceWritePayload(
          { ...params.payload, buttons },
          { isCreate: true },
        ),
      )
      if (!createRes.ok) {
        throw toApiError(createRes.data, 'Failed to clone device')
      }
      const device = createRes.data as ProvisioningDeviceDetail

      try {
        const reloadRes = await api.reloadDeviceConfig(pbxId, [device.id])
        if (!reloadRes.ok) {
          console.warn(
            'Initial config reload returned non-ok after device clone',
          )
        }
      } catch (err) {
        console.warn('Initial config reload failed after device clone:', err)
      }

      return device
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DEVICE_QUERY_KEYS.lists() })
    },
  })
}
